<section class="actions-section" aria-labelledby="actions-heading">
  <header class="actions-heading">
    <div class="actions-icon" aria-hidden="true">
      <mat-icon>flag</mat-icon>
    </div>
    <div>
      <p class="eyebrow">{{ 'dairyYieldScan.summary.actions.eyebrow' | translate }}</p>
      <h2 id="actions-heading">{{ 'dairyYieldScan.summary.actions.title' | translate }}</h2>
      <p>{{ 'dairyYieldScan.summary.actions.introduction' | translate }}</p>
    </div>
  </header>

  <form class="actions-form" [formGroup]="form">
    <div class="save-indicator" [class.error]="saveState() === 'error'" aria-live="polite">
      @switch (saveState()) {
        @case ('saving') {
          <mat-spinner diameter="18" aria-hidden="true" />
          <span>{{ 'dairyYieldScan.summary.actions.save.saving' | translate }}</span>
        }
        @case ('saved') {
          <mat-icon aria-hidden="true">check_circle</mat-icon>
          <span>{{ 'dairyYieldScan.summary.actions.save.saved' | translate }}</span>
        }
        @case ('error') {
          <mat-icon aria-hidden="true">error_outline</mat-icon>
          <span>{{ 'dairyYieldScan.summary.actions.save.error' | translate }}</span>
          <button mat-button type="button" (click)="retrySave()">
            {{ 'dairyYieldScan.summary.actions.save.retry' | translate }}
          </button>
        }
        @case ('dirty') {
          <mat-icon aria-hidden="true">edit</mat-icon>
          <span>{{ 'dairyYieldScan.summary.actions.save.dirty' | translate }}</span>
        }
        @default {
          <mat-icon aria-hidden="true">cloud_queue</mat-icon>
          <span>{{ 'dairyYieldScan.summary.actions.save.idle' | translate }}</span>
        }
      }
    </div>

    <div class="priority-list">
      @for (priority of priorityFields; track priority.controlName; let priorityIndex = $index) {
        <div class="priority-row">
          <span class="priority-number" [class.optional]="!priority.required" aria-hidden="true">
            {{ priorityIndex + 1 }}
          </span>
          <div class="priority-inputs">
            <mat-form-field appearance="outline">
              <mat-label>{{ priority.labelKey | translate }}</mat-label>
              <mat-select [formControlName]="priority.controlName" [required]="priority.required">
                @if (!priority.required) {
                  <mat-option [value]="null">
                    {{ 'dairyYieldScan.summary.actions.noExtraPriority' | translate }}
                  </mat-option>
                }
                @for (improvement of improvements; track improvement.questionKey) {
                  <mat-option
                    [value]="improvement.questionKey"
                    [disabled]="
                      isPriorityUnavailable(improvement.questionKey, priority.controlName)
                    "
                  >
                    <span>{{ improvement.questionKey + '.actionpoint' | translate }}</span>
                    <strong class="option-potential">
                      + {{ improvement.potential | number: '1.1-1' }} kg
                    </strong>
                  </mat-option>
                }
                <mat-option [value]="'other'">
                  {{ 'dairyYieldScan.summary.actions.otherPriority' | translate }}
                </mat-option>
              </mat-select>
              @if (form.controls[priority.controlName].hasError('required')) {
                <mat-error>{{ 'dairyYieldScan.summary.actions.required' | translate }}</mat-error>
              }
            </mat-form-field>
            @if (isOtherPriority(priority.controlName)) {
              <mat-form-field appearance="outline">
                <mat-label>{{
                  'dairyYieldScan.summary.actions.otherPriorityText' | translate
                }}</mat-label>
                <input
                  id="other-priority-{{ priorityIndex }}"
                  matInput
                  [formControlName]="priority.otherControlName"
                  maxlength="200"
                  [placeholder]="'dairyYieldScan.summary.actions.otherPriorityText' | translate"
                  required
                />
              </mat-form-field>
            }
          </div>
        </div>
      }
    </div>

    <section class="impact-summary" aria-labelledby="impact-summary-title">
      <header class="impact-summary-heading">
        <h3 id="impact-summary-title">
          {{ 'dairyYieldScan.summary.actions.impact.title' | translate }}
        </h3>
        @if (hasMilkYieldData()) {
          <div class="impact-target">
            <span>{{ 'dairyYieldScan.summary.actions.impact.target' | translate }}</span>
            <strong>
              {{ targetMilkYield | number: '1.1-1' }}
              {{ 'dairyYieldScan.summary.actions.impact.unit' | translate }}
            </strong>
            @if (remainingToTarget() !== null) {
              <small>
                {{
                  'dairyYieldScan.summary.actions.impact.remainingToTarget'
                    | translate: { value: (remainingToTarget() | number: '1.1-1') }
                }}
              </small>
            }
          </div>
        }
      </header>

      @if (hasMilkYieldData()) {
        <div class="impact-equation">
          <article>
            <small>{{
              'dairyYieldScan.summary.actions.impact.currentProduction' | translate
            }}</small>
            <strong>{{ currentMilkYield | number: '1.1-1' }}</strong>
            <span>{{ 'dairyYieldScan.summary.actions.impact.unit' | translate }}</span>
          </article>
          <div class="equation-symbol" aria-hidden="true">+</div>
          <article class="action-value">
            <small>{{ 'dairyYieldScan.summary.actions.impact.selectedActions' | translate }}</small>
            <strong>+{{ selectedActionPotential() | number: '1.1-1' }}</strong>
            <span>{{ 'dairyYieldScan.summary.actions.impact.unit' | translate }}</span>
          </article>
          <div class="equation-symbol" aria-hidden="true">=</div>
          <article class="improved-value">
            <small>{{
              'dairyYieldScan.summary.actions.impact.improvedProduction' | translate
            }}</small>
            <strong>{{ improvedMilkProduction() | number: '1.1-1' }}</strong>
            <span>{{ 'dairyYieldScan.summary.actions.impact.unit' | translate }}</span>
          </article>
        </div>
      } @else {
        <p class="impact-unavailable">
          {{ 'dairyYieldScan.summary.actions.impact.unavailable' | translate }}
        </p>
      }
    </section>

    <section class="agreement-panel" aria-labelledby="agreement-title">
      <h3 id="agreement-title">
        {{ 'dairyYieldScan.summary.actions.agreementTitle' | translate }}
      </h3>
      <p>{{ 'dairyYieldScan.summary.actions.agreementIntroduction' | translate }}</p>
      <div class="agreement-grid">
        <mat-form-field appearance="outline">
          <mat-label>{{ 'dairyYieldScan.summary.actions.farmerAction' | translate }}</mat-label>
          <label class="cdk-visually-hidden" for="farmer-action">
            {{ 'dairyYieldScan.summary.actions.farmerAction' | translate }}
          </label>
          <textarea
            id="farmer-action"
            matInput
            formControlName="farmerAction"
            maxlength="1000"
            rows="5"
            [placeholder]="'dairyYieldScan.summary.actions.farmerPlaceholder' | translate"
          ></textarea>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>{{ 'dairyYieldScan.summary.actions.advisorAction' | translate }}</mat-label>
          <label class="cdk-visually-hidden" for="advisor-action">
            {{ 'dairyYieldScan.summary.actions.advisorAction' | translate }}
          </label>
          <textarea
            id="advisor-action"
            matInput
            formControlName="advisorAction"
            maxlength="1000"
            rows="5"
            [placeholder]="'dairyYieldScan.summary.actions.advisorPlaceholder' | translate"
          ></textarea>
        </mat-form-field>
      </div>
      <mat-form-field class="evaluation-date" appearance="outline">
        <mat-label>{{ 'dairyYieldScan.summary.actions.evaluationDate' | translate }}</mat-label>
        <input
          matInput
          formControlName="evaluationDate"
          [matDatepicker]="evaluationDatePicker"
          [matDatepickerFilter]="evaluationDateFilter"
        />
        <mat-datepicker-toggle matIconSuffix [for]="evaluationDatePicker" />
        <mat-datepicker #evaluationDatePicker />
        <mat-hint>{{ 'dairyYieldScan.summary.actions.evaluationDateHelp' | translate }}</mat-hint>
        @if (form.controls.evaluationDate.invalid) {
          <mat-error>{{
            'dairyYieldScan.summary.actions.invalidEvaluationDate' | translate
          }}</mat-error>
        }
      </mat-form-field>
    </section>
  </form>
</section>
